import i18next from 'i18next'

import { API } from '@/api'
import { CredentialResourceType } from '@/api/type-server'
import { canBindCredential } from '@/utils/credential'
import { getErrorCodeMessage } from '@/utils/error'
import type { RequestConf } from '@/utils/http'

// These dialogs own their error message, including errors ignored by the shared interceptor.
export async function requestCredential<T extends { code: number }>(
  request: (config: RequestConf) => Promise<T>,
): Promise<T> {
  let response: T
  try {
    response = await request({ errMsg: false, noErrMsg: true })
  } catch {
    throw new Error(i18next.t('accessKeys:errors.requestFailed'))
  }
  if (response?.code !== 0) {
    throw new Error(
      response?.code > 0 && response.code !== 1
        ? getErrorCodeMessage(response.code)
        : i18next.t('accessKeys:errors.requestFailed'),
    )
  }
  return response
}

export async function updateCredentialBindings(
  resID: string,
  strategy: 'auto' | 'manual',
  selectedIds: Set<string>,
) {
  // Read again on every submit so retries use the actual state after a partial failure.
  const { credential } = await requestCredential((config) =>
    API.getCredential({ res_id: resID }, config),
  )
  const resources = credential.resources || []
  const hasAuto = resources.some((r) => r.resourceType === CredentialResourceType.DeploymentClass)
  const boundIds = new Set(
    resources
      .filter((r) => r.resourceType === CredentialResourceType.Deployment)
      .flatMap((r) => r.resIDs || []),
  )
  const toBind = [...selectedIds].filter((id) => !boundIds.has(id))
  const toUnbind = [...boundIds].filter((id) => strategy === 'auto' || !selectedIds.has(id))
  const addsBinding = strategy === 'auto' ? !hasAuto : toBind.length > 0
  if (addsBinding && !canBindCredential(credential)) {
    throw new Error(i18next.t('accessKeys:errors.bindingUnavailable'))
  }

  let changed = false
  const mutate = async (bind: boolean, resType: CredentialResourceType, ids: string[]) => {
    await requestCredential((config) =>
      (bind ? API.bindCredentialResource : API.unbindCredentialResource)(
        { res_id: resID, resources: [{ res_ids: ids, res_type: resType }] },
        config,
      ),
    )
    changed = true
  }
  try {
    if (strategy === 'auto') {
      if (toUnbind.length) await mutate(false, CredentialResourceType.Deployment, toUnbind)
      if (!hasAuto) await mutate(true, CredentialResourceType.DeploymentClass, [])
    } else {
      if (hasAuto) await mutate(false, CredentialResourceType.DeploymentClass, [])
      if (toBind.length) await mutate(true, CredentialResourceType.Deployment, toBind)
      if (toUnbind.length) await mutate(false, CredentialResourceType.Deployment, toUnbind)
    }
  } catch (error) {
    if (changed) throw new Error(i18next.t('accessKeys:errors.partialBindingFailure'))
    throw error
  }
}
